'use client'

import { useEffect, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Download,
  FileArchive,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

type Moderation = {
  status: string
  decision: string | null
  riskLevel: 'low' | 'caution' | 'danger'
  reasons: string[]
  categories: string[]
  confidence: number | null
  error: string | null
}

type FileDrop = {
  id: number
  originalName: string
  description: string
  mimeType: string
  detectedType: string
  sizeBytes: number
  sha256: string
  status: string
  uploaderHandle: string
  createdAt: string
  downloadUrl: string | null
  moderation: Moderation | null
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDate(value: string) {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function AdminFileDrops() {
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [files, setFiles] = useState<FileDrop[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function loadFiles() {
    const response = await fetch('/api/admin/file-drops')
    if (!response.ok) {
      setAuthorized(false)
      return
    }
    const data = (await response.json()) as { files?: FileDrop[] }
    setAuthorized(true)
    setFiles(data.files || [])
  }

  useEffect(() => {
    loadFiles()
      .catch(() => setAuthorized(false))
      .finally(() => setLoading(false))
    const timer = window.setInterval(() => {
      void loadFiles().catch(() => {
        // Keep the current queue visible during a transient refresh failure.
      })
    }, 30_000)
    return () => window.clearInterval(timer)
  }, [])

  async function review(file: FileDrop, action: 'approve' | 'reject') {
    if (
      action === 'reject' &&
      !window.confirm(`Reject and permanently delete ${file.originalName}?`)
    ) {
      return
    }
    setBusyId(file.id)
    setError('')
    setNotice('')
    try {
      const response = await fetch(
        action === 'approve'
          ? `/api/admin/file-drops/${file.id}/approve`
          : `/api/admin/file-drops/${file.id}`,
        {
          method: action === 'approve' ? 'POST' : 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note:
              action === 'approve'
                ? 'Reviewed and approved by an administrator. No byte-level antivirus scan was performed.'
                : 'Rejected by an administrator. The quarantined file was deleted.',
          }),
        },
      )
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not update the file.')
      setNotice(action === 'approve' ? 'File approved and published.' : 'File rejected and deleted.')
      await loadFiles()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the file.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-lg font-semibold">File-drop quarantine</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Every upload waits here until you approve it. DeepSeek reviews metadata only and
              cannot prove that a binary is malware-free.
            </p>
          </div>
        </div>
        <span className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground">
          {files.length} awaiting review
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : authorized === false ? (
        <p className="mt-6 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Administrator access is required.
        </p>
      ) : files.length === 0 ? (
        <p className="mt-6 rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          There are no files waiting for review.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {files.map((file) => {
            const risk = file.moderation?.riskLevel || 'caution'
            const riskClass =
              risk === 'danger'
                ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
                : risk === 'caution'
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                  : 'border-border bg-secondary text-secondary-foreground'
            return (
              <article key={file.id} className="rounded-xl border border-border bg-background p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-primary">
                      <FileArchive className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="break-all font-semibold">{file.originalName}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatFileSize(file.sizeBytes)} · {file.detectedType.replaceAll('-', ' ')} ·
                        uploaded by {file.uploaderHandle} · {formatDate(file.createdAt)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${riskClass}`}
                  >
                    {risk === 'danger' ? (
                      <ShieldAlert className="h-3.5 w-3.5" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    )}
                    {risk === 'danger' ? 'High risk' : risk === 'caution' ? 'Review carefully' : 'No metadata flags'}
                  </span>
                </div>

                {file.description && (
                  <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
                    {file.description}
                  </p>
                )}

                <div className="mt-3 rounded-lg border border-border bg-card px-3 py-2">
                  <p className="flex items-center gap-1.5 text-xs font-medium">
                    {file.moderation?.status === 'completed' ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                    ) : file.moderation?.status === 'error' ? (
                      <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                    ) : (
                      <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    Metadata scan: {file.moderation?.status || 'not queued'}
                    {file.moderation?.confidence !== null &&
                      file.moderation?.confidence !== undefined &&
                      ` · ${Math.round(file.moderation.confidence * 100)}% confidence`}
                  </p>
                  {file.moderation?.reasons?.length ? (
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                      {file.moderation.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  ) : null}
                  {file.moderation?.error && (
                    <p className="mt-1 text-xs text-destructive">{file.moderation.error}</p>
                  )}
                  {file.moderation?.categories?.length ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Categories: {file.moderation.categories.join(', ')}
                    </p>
                  ) : null}
                </div>

                <p className="mt-3 truncate font-mono text-[11px] text-muted-foreground" title={file.sha256}>
                  SHA-256: {file.sha256}
                </p>

                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {file.downloadUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      nativeButton={false}
                      render={<a href={file.downloadUrl} />}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download for review
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive"
                    disabled={busyId === file.id}
                    onClick={() => void review(file, 'reject')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    disabled={busyId === file.id}
                    onClick={() => void review(file, 'approve')}
                  >
                    {busyId === file.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    Approve &amp; publish
                  </Button>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-3 text-sm text-primary">
          {notice}
        </p>
      )}
    </section>
  )
}
